<template>
  <!-- Pinned to the viewport rather than sized to it: an h-screen shell still
       leaves a 100vh-tall box in the document, so anything that shortens the
       visual viewport (a horizontal scrollbar, a mobile toolbar, zoom rounding)
       gives the page a second, outer scrollbar. Fixed keeps body height at 0, so
       the only scrollbar is the panel's own content scroller below. -->
  <div class="fixed inset-0 flex bg-white text-foreground overflow-hidden">
    <!-- ─── Sidebar ─────────────────────────────────────────────────────────── -->
    <aside
      class="fixed inset-y-0 left-0 z-50 bg-white border-r border-gray-200/80 flex flex-col py-5 overflow-hidden transition-all duration-300 ease-in-out"
      :class="
        sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
      "
      :style="{ width: sidebarWidth + 'px' }"
      @mouseenter="onSidebarEnter"
      @mouseleave="onSidebarLeave"
    >
      <!-- Logo -->
      <div
        class="px-3 w-full flex justify-center gap-2 pt-1 pb-6 bg-white sticky top-0"
      >
        <RouterLink
          to="/teachers"
          class="inline-flex flex-col items-end justify-center opacity-100 hover:opacity-80 transition-opacity outline-none"
        >
          <img
            :src="expanded ? logoFull : logoMicro"
            alt="sortmyprep logo"
            class="transition-all duration-300"
            :class="expanded ? 'w-[111px]' : 'h-8 w-auto'"
          />
        </RouterLink>
      </div>

      <!-- Organisation switcher - only rendered for multi-org accounts -->
      <!-- <OrganisationSwitcher :expanded="expanded" /> -->

      <!-- Nav items -->
      <nav class="flex-1 w-full px-3 space-y-0.5">
        <button
          v-for="item in navItems"
          :key="item.key"
          class="w-full flex items-center gap-3 py-2.5 px-3 rounded-lg transition-all duration-200 group relative border"
          :class="
            activeNav === item.key
              ? 'bg-[#E0EDFF] text-[#2563EB] border-[#4A8EEE]/30 shadow-sm'
              : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50 border-transparent'
          "
          :title="expanded ? '' : item.label"
          @click="navigate(item)"
        >
          <component
            :is="item.icon"
            class="w-[18px] h-[18px] flex-shrink-0 transition-all duration-200"
            :class="
              activeNav === item.key ? 'stroke-[2.5px]' : 'stroke-[1.5px]'
            "
          />
          <span
            class="text-sm leading-tight flex-1 text-left whitespace-nowrap transition-opacity duration-200"
            :class="[
              activeNav === item.key ? 'font-semibold' : 'font-medium',
              expanded ? 'opacity-100' : 'opacity-0',
            ]"
            >{{ item.label }}</span
          >
        </button>
      </nav>

      <!-- Bottom actions -->
      <div
        class="w-full px-3 space-y-0.5 border-t border-gray-200/80 pt-3 mt-3"
      >
        <NotificationBell
          mode="org"
          variant="sidebar"
          :label="expanded ? 'Notifications' : ''"
        />

        <!-- <button
          aria-label="Toggle dark mode"
          :title="
            expanded
              ? ''
              : isDark
                ? 'Switch to light mode'
                : 'Switch to dark mode'
          "
          class="w-full flex items-center gap-3 py-2.5 px-3 rounded-lg transition-all duration-200 text-gray-500 hover:text-gray-800 hover:bg-gray-50 border border-transparent"
          @click="toggleDark"
        >
          <component
            :is="isDark ? Sun : Moon"
            class="w-[18px] h-[18px] flex-shrink-0 stroke-[1.5px]"
          />
          <span
            class="text-sm leading-tight flex-1 text-left font-medium whitespace-nowrap transition-opacity duration-200"
            :class="expanded ? 'opacity-100' : 'opacity-0'"
          >
            {{ isDark ? 'Light Mode' : 'Dark Mode' }}
          </span>
        </button> -->

        <button
          :title="expanded ? '' : 'Logout'"
          class="w-full flex items-center gap-3 py-2.5 px-3 rounded-lg transition-all duration-200 text-gray-500 hover:text-gray-800 hover:bg-gray-50 border border-transparent"
          @click="logoutModalVisible = true"
        >
          <LogOut class="w-[18px] h-[18px] flex-shrink-0 stroke-[1.5px]" />
          <span
            class="text-sm leading-tight flex-1 text-left font-medium whitespace-nowrap transition-opacity duration-200"
            :class="expanded ? 'opacity-100' : 'opacity-0'"
            >Logout</span
          >
        </button>
      </div>
    </aside>

    <!-- Invisible hover trigger that expands the collapsed review-page sidebar.
         Rendered only while collapsed so it never blocks content clicks once the
         sidebar is open; it sits flush against the rail and reaches a little into
         the content so the pointer needn't be pixel-perfect. -->
    <div
      v-if="collapsibleActive && !expanded"
      class="hidden lg:block fixed inset-y-0 z-50"
      :style="{ left: sidebarWidth + 'px', width: '28px' }"
      aria-hidden="true"
      @mouseenter="onSidebarEnter"
    />

    <!-- Mobile overlay backdrop -->
    <Transition name="fade">
      <div
        v-if="sidebarOpen"
        class="fixed inset-0 z-40 bg-black/20 lg:hidden"
        @click="sidebarOpen = false"
      />
    </Transition>

    <!-- ─── Main area ─────────────────────────────────────────────────────── -->
    <div
      class="flex-1 flex flex-col overflow-hidden transition-all duration-300 ease-in-out"
      :style="{ marginLeft: sidebarWidth + 'px' }"
    >
      <!-- Mobile top bar (hidden on lg+) -->
      <header
        class="lg:hidden flex items-center gap-3 px-4 py-3 border-b border-gray-200 bg-white sticky top-0 z-30 shrink-0"
      >
        <button
          class="p-1.5 rounded-md text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition-colors"
          @click="sidebarOpen = !sidebarOpen"
        >
          <Menu class="w-5 h-5" />
        </button>
        <RouterLink
          to="/"
          class="inline-flex flex-col items-end justify-center opacity-100 hover:opacity-80 transition-opacity outline-none"
        >
          <img
            src="@/assets/images/logo.svg"
            alt="sortmyprep logo"
            class="w-[111px]"
          />
        </RouterLink>
      </header>

      <!-- Scrollable content -->
      <div class="flex-1 overflow-y-auto">
        <!-- Child routes render here. Held back until an organisation is
             chosen, so no view fires a request that cannot name one. -->
        <router-view v-if="!needsOrganisationChoice" />

        <!-- ── Fallback home when no child route is active ── -->
        <div v-if="showFallback" class="min-h-full bg-white pb-20">
          <div
            class="px-6 md:px-8 py-6 md:py-8 max-w-7xl mx-auto w-full space-y-6"
          >
            <!-- Welcome -->
            <div>
              <h1 class="text-xl md:text-2xl font-bold text-[#1E3456] mb-0.5">
                Welcome back 👋
              </h1>
              <p class="text-sm text-gray-500">
                Select a section from the sidebar to get started.
              </p>
            </div>

            <!-- Quick-access cards grid -->
            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <button
                v-for="item in navItems"
                :key="item.key"
                class="group text-left border border-gray-200 rounded-xl p-5 shadow-sm hover:shadow-md hover:border-[#4A8EEE]/40 transition-all duration-200 bg-white"
                @click="navigate(item)"
              >
                <div
                  class="w-10 h-10 rounded-lg flex items-center justify-center mb-3 bg-gray-50 group-hover:bg-[#E0EDFF] transition-colors duration-200"
                >
                  <component
                    :is="item.icon"
                    class="w-5 h-5 text-gray-400 group-hover:text-[#4A8EEE] transition-colors duration-200"
                  />
                </div>
                <p class="text-sm font-semibold text-[#1E3456] mb-0.5">
                  {{ item.label }}
                </p>
                <p class="text-xs text-gray-400 leading-snug">
                  {{ item.description }}
                </p>
              </button>
            </div>

            <!-- Stats strip -->
            <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div
                v-for="stat in stats"
                :key="stat.label"
                class="bg-white border border-gray-200 rounded-xl p-5 shadow-sm"
              >
                <div class="flex items-center justify-between mb-3">
                  <span
                    class="text-xs font-semibold text-gray-500 uppercase tracking-wider"
                    >{{ stat.label }}</span
                  >
                  <div
                    class="w-8 h-8 rounded-lg flex items-center justify-center"
                    :class="stat.iconBg"
                  >
                    <component
                      :is="stat.icon"
                      class="w-4 h-4"
                      :class="stat.iconColor"
                    />
                  </div>
                </div>
                <p class="text-2xl font-bold text-[#1E3456]">
                  {{ stat.value }}
                </p>
                <p class="text-xs text-gray-400 mt-1">{{ stat.sub }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <a-modal
      v-model:open="logoutModalVisible"
      :title="null"
      :footer="null"
      :width="500"
      :centered="true"
      destroy-on-close
    >
      <div>
        <!-- Header -->
        <div class="flex items-start gap-4">
          <div
            class="w-12 h-12 rounded-xl bg-red-50 border border-red-100 flex items-center justify-center flex-shrink-0"
          >
            <LogOut class="w-6 h-6 text-red-500" />
          </div>
          <div>
            <h2 class="text-xl font-bold text-[#1E3456]">Logout</h2>
            <p class="text-sm text-gray-500 mt-1">
              Are you sure you want to logout from your account?
            </p>
          </div>
        </div>
        <!-- Warning Box -->
        <div class="mt-5 rounded-xl border border-amber-200 bg-amber-50 p-4">
          <div class="font-medium text-amber-800">Current session will end</div>
          <div class="text-sm text-amber-700 mt-1">
            You will need to sign in again.
          </div>
        </div>
        <!-- Footer -->
        <div class="flex justify-end gap-3 mt-6">
          <button
            class="inline-flex items-center justify-center h-9 px-4 rounded-md border border-gray-200 text-sm font-medium text-[#1E3456] hover:bg-gray-50 transition-colors"
            @click="logoutModalVisible = false"
          >
            Cancel
          </button>
          <button
            class="inline-flex items-center justify-center gap-2 h-9 px-4 rounded-md bg-red-500 hover:bg-red-600 text-white text-sm font-medium transition-colors"
            @click="confirmLogout"
          >
            <LogOut class="w-4 h-4" />
            Logout
          </button>
        </div>
      </div>
    </a-modal>
    <!-- Multi-org accounts pick which organisation they are acting on before
         the panel loads; a single-org account never sees this. -->
    <OrganisationSelectModal :is-open="needsOrganisationChoice" />
  </div>
</template>

<script setup>
import {
  Users,
  FileText,
  GraduationCap,
  Building2,
  Moon,
  Sun,
  LogOut,
  Menu,
  BookOpen,
  ShieldCheck,
} from 'lucide-vue-next';
import logoFull from '@/assets/images/logo.svg';
import logoMicro from '@/assets/images/logo_micro.svg';

const router = useRouter();
const route = useRoute();

const orgAuthStore = useAuthOrganisationStore();
const { needsOrganisationChoice } = storeToRefs(orgAuthStore);

// ─── Sidebar ───────────────────────────────────────────────────────────────
const sidebarOpen = ref(false);
const isDark = ref(false);
const logoutModalVisible = ref(false);

// ─── Collapsible hover mode (Grade for Me review page only) ─────────────────
// On the full-page review the sidebar collapses to an icon rail so the grading
// workspace gets more room, expanding on hover. Every other org view keeps the
// permanently-expanded sidebar, so this is gated to that single route and to
// desktop widths (hover is meaningless on touch, where the sidebar is a drawer).
const COLLAPSED_WIDTH = 72;
const EXPANDED_WIDTH = 230;

const isDesktop = useMediaQuery('(min-width: 1024px)');
const hovered = ref(false);

const collapsibleActive = computed(
  () => route.name === 'org-grade-for-me-review' && isDesktop.value
);
const expanded = computed(() => !collapsibleActive.value || hovered.value);
const sidebarWidth = computed(() =>
  expanded.value ? EXPANDED_WIDTH : COLLAPSED_WIDTH
);

function onSidebarEnter() {
  if (collapsibleActive.value) hovered.value = true;
}
function onSidebarLeave() {
  if (collapsibleActive.value) hovered.value = false;
}

// Drop any stale hover state when navigating away from the review page.
watch(collapsibleActive, (active) => {
  if (!active) hovered.value = false;
});

// ─── Nav config ────────────────────────────────────────────────────────────
const navItems = [
  {
    key: 'classroom',
    label: 'My Classroom',
    icon: Users,
    route: '/org/classroom',
    description: 'Manage students and class groups',
  },
  {
    key: 'worksheet',
    label: 'Worksheet Builder',
    icon: FileText,
    route: '/org/worksheet-builder',
    description: 'Build and assign custom worksheets',
  },
  {
    key: 'grade',
    label: 'Grade for me',
    icon: GraduationCap,
    route: '/org/grade-for-me',
    description: 'AI-assisted grading for submissions',
  },
  {
    key: 'organisation',
    label: 'Manage Organisation',
    icon: Building2,
    route: '/org/manage-organisation',
    description: 'Teachers, admins, and org settings',
  },
];

// ─── Derived active state from current route ───────────────────────────────
const activeNav = computed(() => {
  const match = navItems.find((item) => route.path.startsWith(item.route));
  return match?.key ?? '';
});

// Show welcome home only when sitting on the bare /org route with no child matched
const showFallback = computed(
  () => route.path === '/org' || route.path === '/org/'
);

// ─── Stats for welcome home ────────────────────────────────────────────────
const stats = [
  {
    label: 'Teachers',
    value: '6',
    sub: 'Active this month',
    icon: Users,
    iconBg: 'bg-blue-50',
    iconColor: 'text-[#4A8EEE]',
  },
  {
    label: 'Admins',
    value: '3',
    sub: 'Organisation admins',
    icon: ShieldCheck,
    iconBg: 'bg-purple-50',
    iconColor: 'text-purple-500',
  },
  {
    label: 'Subjects',
    value: '9',
    sub: 'Across all boards',
    icon: BookOpen,
    iconBg: 'bg-green-50',
    iconColor: 'text-green-500',
  },
  {
    label: 'Boards',
    value: '4',
    sub: 'A Level, IGCSE, IBDP…',
    icon: GraduationCap,
    iconBg: 'bg-amber-50',
    iconColor: 'text-amber-500',
  },
];

// ─── Methods ───────────────────────────────────────────────────────────────
function navigate(item) {
  router.push(item.route);
  sidebarOpen.value = false;
}

function toggleDark() {
  isDark.value = !isDark.value;
  document.documentElement.classList.toggle('dark', isDark.value);
}

function logout() {
  const orgAuth = useAuthOrganisationStore();
  orgAuth.logoutOrganisation(() => router.push('/teachers'));
}

function confirmLogout() {
  logoutModalVisible.value = false;
  logout();
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Remove left margin on mobile */
@media (max-width: 1023px) {
  .flex-1[style*='margin-left'] {
    margin-left: 0 !important;
  }
}
</style>
